import React, { useEffect, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import StatCard from "src/components/reusable/StatCard";
import StyledTable from "../../../components/reusable/StyledTable";
import {
  mostConnectedColumns,
  mostViewedColumns,
  apiHealthColumns,
  SourceMetric,
  getIconForPriority,
} from "./AdminDashboardColumns"; // adjust the path accordingly
import {
  getDashboardMetrics,
  triggerAPIHealthCheck,
  getAPIHealthStatus,
  getSystemAlerts,
} from "../../../services/AdminPortalAPIs";

const { RangePicker } = DatePicker;

const Dashboard = () => {
  const [metrics, setMetrics] = useState<any>(null);
  const [alerts, setAlerts] = useState<any[]>([]);
  const systemAlertRef = React.useRef(false);
  const sourceConnectRef = React.useRef(false);
  const [dates, setDates] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(() => {
    const end = dayjs();
    const start = dayjs()
      .month(2)
      .date(1);
    return [start, end];
  });
  const [apiHealth, setApiHealth] = useState<any[]>([]);
  const [loadingHealth, setLoadingHealth] = useState(false);

  const fetchSystemAlerts = async () => {
    try {
      const res = await getSystemAlerts();
      if (res.data.status) {
        setAlerts(res.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch system alerts:", error);
    }
  };

  const fetchAPIHealthStatus = async () => {
    setLoadingHealth(true);
    try {
      const res = await getAPIHealthStatus();
      if (res.data.status) {
        setApiHealth(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching API health status:", error);
    } finally {
      setLoadingHealth(false);
    }
  };

  const refreshAPIHealth = async () => {
    setLoadingHealth(true);
    try {
      const res = await triggerAPIHealthCheck();
      if (res.data.status) {
        setApiHealth(res.data.data);
      }
    } catch (error) {
      console.error("Error refreshing API health:", error);
    } finally {
      setLoadingHealth(false);
    }
  };

  useEffect(() => {
    if (systemAlertRef.current) {return;}
    systemAlertRef.current = true;
    fetchSystemAlerts();
  }, []);

  useEffect(() => {
    if (sourceConnectRef.current) {return;}
    sourceConnectRef.current = true;
    fetchAPIHealthStatus();
  }, []);

  const fetchMetrics = async (start: string, end: string) => {
    try {
      const response = await getDashboardMetrics({
        start_date: start,
        end_date: end,
      });
      if (response.data?.status) {
        setMetrics(response.data.metrics);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard metrics:", error);
    }
  };

  useEffect(() => {
    fetchMetrics(dates[0].format("YYYY-MM-DD"), dates[1].format("YYYY-MM-DD"));
  }, [dates]);

  useEffect(() => {
    if (metrics) {
    }
  }, [metrics]);

  if (!metrics) {
    return <LoadingSpinner />;
  }

  const mostConnectedSources: SourceMetric[] = (
    metrics.most_connected_sources || []
  ).map((item: any) => ({
    source: item.source_name,
    users: item.connection_count,
    change:
      typeof item.change === "string" && item.change.trim() !== ""
        ? item.change
        : "0.0%",
  }));

  return (
    <div className="admin-portal-page">
      <span className="heading-primary ">Dashboard</span>

      <div className="filters">
        <RangePicker
          value={dates}
          onChange={range => {
            if (range) {
              setDates(range as [dayjs.Dayjs, dayjs.Dayjs]);
            }
          }}
        />
      </div>

      <div className="stats-grid field-labels">
        <StatCard
          label="Total Users"
          value={metrics.total_users.count}
          description={metrics.total_users.change}
        />
        <StatCard
          label="Active Users"
          value={metrics.active_users.count}
          description={metrics.active_users.change}
        />
        <StatCard
          label="Connected Sources"
          value={metrics.connected_sources.count}
          description={metrics.connected_sources.change}
        />
        <StatCard
          label="Dashboard Views"
          value={metrics.dashboard_views.count}
          description={metrics.dashboard_views.change}
        />
      </div>

      <div className="panel-row">
        <div className="panel-card blue-outline-card ">
          <div className="panel-header">Most Connected Sources</div>
          <StyledTable
            columns={mostConnectedColumns}
            dataSource={mostConnectedSources}
            rowKey={record => record.source}
            pagination={false}
            locale={{ emptyText: "No source connection data available." }}
          />
        </div>

        <div className="panel-card blue-outline-card">
          <div className="panel-header">Most Viewed Dashboards</div>
          <StyledTable
            columns={mostViewedColumns}
            dataSource={metrics.most_viewed_dashboards}
            pagination={false}
            rowKey="dashboard_name"
          />
        </div>
      </div>
      <div className="alerts-health-container">
        <div className="alert-panel blue-outline-card">
          <div className="panel-header-row">
            <h3>System Alerts (Last 24h)</h3>
          </div>

          {alerts.length === 0 ? (
            <p style={{ color: "#999" }}>No alerts found.</p>
          ) : (
            alerts.map(alert => (
              <div
                key={alert.alert_id}
                className={`alert-card ${alert.priority.toLowerCase()}`} // e.g., "high", "medium"
              >
                <div className="alert-icon">
                  {getIconForPriority(alert.priority)}
                </div>

                <div className="alert-content">
                  <strong>{alert.title}</strong>
                  <p>{alert.details}</p>
                  <span className="alert-meta">{alert.time_ago}</span>
                </div>
                <span className={`alert-tag ${alert.priority.toLowerCase()}`}>
                  {alert.priority}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="api-health-panel blue-outline-card ">
          <div className="panel-header-row">
            <h3>Sources Endpoint Status</h3>
            <button
              className="btn-refresh"
              onClick={refreshAPIHealth}
              aria-label="Refresh API Health"
              type="button"
            >
              🔄 Refresh
            </button>
          </div>

          {loadingHealth ? (
            <LoadingSpinner />
          ) : (
            <StyledTable
              columns={apiHealthColumns}
              dataSource={apiHealth}
              pagination={false}
              rowKey="service"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
